import { eventRepository } from "@/event/integration/game/eventCollectableRepository";
import { resetEventUpgradeLevels } from "@/event/integration/game/resetEventUpgradeLevels";
import { isPastEndDate } from "@/event/session/eventEndDate";
import { persistEventUpgradeReset } from "@/event/session/eventEndTeardownPersist";
import { getStarterBuildingGrants } from "@/event/session/eventStarterBuildingRemoval";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import type { EventStarterBuildingGrant } from "@/types";

export const EVENTIUM_RESOURCE_TYPES: Array<ResourceType> = [
	ResourceType.EVENTIUM,
	ResourceType.EVENTIUM_INCOME,
	ResourceType.EVENTIUM_UNCAPPED_INCOME,
	ResourceType.EVENTIUM_UNCAPPED_INCOME_WITHOUT_PENALTIES,
	ResourceType.EVENTIUM_ACCUMULATED,
	ResourceType.EVENTIUM_CONSUMPTION,
	ResourceType.EVENTIUM_PLUS,
	ResourceType.EVENTIUM_MINUS,
];

export { isPastEndDate } from "@/event/session/eventEndDate";

function cleanupDedupeKey(parts: Array<string>): string {
	return parts.join("|");
}

const VALID_BUILDINGS = new Set<string>(
	Object.values(FrameName) as Array<string>,
);

function clearEventiumInStores(): void {
	const resources = useResourcesStore();
	for (const type of EVENTIUM_RESOURCE_TYPES) {
		resources.set(type, 0, false);
	}
}

function clearStarterBuildingsInStorage(buildings: ReadonlySet<string>): void {
	const current = useBuildingsStorageStore().getAllBuildings();
	for (const frameName of buildings) {
		if (!VALID_BUILDINGS.has(frameName)) continue;
		if ((current[frameName as FrameName] ?? 0) === 0) continue;
		useBuildingsStorageStore().decrease(
			frameName as FrameName,
			current[frameName as FrameName],
			false,
		);
	}
}

/** Strip eventium fields from a load/save payload when no event is playable. */
export function zeroEventiumInResourcePatch(
	resources: Partial<Record<ResourceType, number>> | undefined,
): void {
	if (!resources) return;
	for (const type of EVENTIUM_RESOURCE_TYPES) {
		resources[type] = 0;
	}
}

/**
 * Load-time guard: strip event-scoped resources only when the server overview says
 * there is no active event window — not when `isActive` is false because
 * settlements (main level) have not been patched yet.
 */
export function shouldStripEventResourcesOnLoad(params: {
	eventId: string | null;
	endDate: Date | null;
	serverEventActive: boolean;
}): boolean {
	if (params.eventId === null) return true;
	if (isPastEndDate(params.endDate)) return true;
	return !params.serverEventActive;
}

/** Removes Easter collectable labels still visible after the event ended. */
export function clearEndedEventCollectables(): void {
	eventRepository.clearSpawnedCollectablesFromMap();
}

/** Clears event upgrade levels locally once admin `endDate` has passed. */
export function clearEndedEventUpgrades(): void {
	resetEventUpgradeLevels();
	void persistEventUpgradeReset();
}

/**
 * Clears only the starter buildings granted for the ending event type from
 * local inventory/storage. Placed tiles stay on the map until same-type start.
 */
export function clearEndedEventStarterBuildings(
	params: {
		eventType: string | null | undefined;
		startBuildingClaims?: ReadonlyArray<EventStarterBuildingGrant> | null;
		overviewGrants?: ReadonlyArray<EventStarterBuildingGrant> | null;
		eventId?: string | null | undefined;
	},
	onceKeys: Set<string>,
): boolean {
	if (!params.eventType || !params.eventId) return false;

	const grants = getStarterBuildingGrants({
		startBuildingClaims: params.startBuildingClaims,
		overviewGrants: params.overviewGrants,
	});
	if (grants.length === 0) return false;

	const dedupe = cleanupDedupeKey([
		"end-starters",
		params.eventId,
		params.eventType,
		...grants.map(grant => grant.frameName.trim()).sort(),
	]);
	if (onceKeys.has(dedupe)) return false;

	const frameNames = new Set<string>();
	for (const grant of grants) {
		const raw = grant.frameName?.trim();
		if (!raw) continue;
		frameNames.add(raw);
	}

	if (frameNames.size === 0) return false;
	clearStarterBuildingsInStorage(frameNames);
	onceKeys.add(dedupe);
	return true;
}

/** Zero eventium resources locally once admin `endDate` has passed. */
export function clearEndedEventium(
	params: {
		eventId: string | null | undefined;
		endDate: Date | null | undefined;
		nowMs?: number;
	},
	onceKeys: Set<string>,
): boolean {
	if (!isPastEndDate(params.endDate, params.nowMs)) {
		return false;
	}

	const endMs = (params.endDate as Date).getTime();
	const dedupe = cleanupDedupeKey([
		"ev-res",
		params.eventId ?? "local",
		endMs.toString(),
	]);
	if (onceKeys.has(dedupe)) return false;

	clearEventiumInStores();
	onceKeys.add(dedupe);
	return true;
}

/**
 * Clears eventium when BE reports no live event but the tab missed the calendar
 * window (local endDate never synced).
 */
export function clearEventScopedResourcesWhenInactive(
	onceKeys: Set<string>,
	reason: string,
): boolean {
	const dedupe = cleanupDedupeKey(["inactive", reason]);
	if (onceKeys.has(dedupe)) return false;

	clearEventiumInStores();
	onceKeys.add(dedupe);
	return true;
}

/** Id for ended-event teardown (grant marker reset, persist). Starter buildings are not removed here. */
export function endedEventCleanupId(params: {
	eventId: string | null | undefined;
	eventType: string | null | undefined;
	endDate: Date | null | undefined;
	nowMs?: number;
}): string | null {
	if (!isPastEndDate(params.endDate, params.nowMs)) {
		return null;
	}

	const endMs = (params.endDate as Date).getTime();
	return cleanupDedupeKey([
		"cal",
		params.eventId ?? "local",
		(params.eventType ?? "local").toString(),
		endMs.toString(),
	]);
}
